
<template>
  <div class="leo-uploaded-list">
    <div class="card">
      <h2 class="title">Leo Uploaded List</h2>
      <form class="date-form" @submit.prevent="fetchLeoUploadedList">
        <div class="form-group">
          <label>
            From Date <span class="required">*</span>
            <input
              type="text"
              v-model="fromdate"
              placeholder="dd/mm/yyyy"
              class="date-input"
              required
            />
          </label>
        </div>
        <div class="form-group">
          <label>
            To Date <span class="required">*</span>
            <input
              type="text"
              v-model="todate"
              placeholder="dd/mm/yyyy"
              class="date-input"
              required
            />
          </label>
        </div>
        <button type="submit" class="fetch-btn">Search</button>
      </form>
      <div v-if="loading">Loading...</div>
      <!-- Toast alert for error messages -->
      <div v-if="showToast" class="toast" role="alert">
        <div class="toast-message">{{ error }}</div>
        <button class="toast-close" @click="closeToast" aria-label="Close">×</button>
      </div>
      <table v-if="leoList.length">
        <thead>
          <tr>
            <th v-for="(header, idx) in headers" :key="idx">{{ header }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in leoList" :key="idx">
            <td v-for="header in headers" :key="header">
              <template v-if="['uploaded_file','failed_file','successed_file'].includes(header)">
                <span>{{ item[header] ?? 0 }}</span>
                <!-- uploaded_file: show Zip and Mail icons -->
                <template v-if="header === 'uploaded_file'">
                  <button
                    class="zip-btn"
                    :title="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip') ? 'Downloading...' : 'Download uploaded files (zip)'"
                    :disabled="!(item[header] > 0) || isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip')"
                    @click.prevent="downloadUploadedZip(item)"
                  >
                    <i :class="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip') ? 'fa fa-spinner fa-spin zip-icon' : 'fa fa-file-archive zip-icon'" aria-hidden="true"></i>
                  </button>
                  <button
                    class="zip-btn"
                    :title="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'mail') ? 'Sending...' : 'Send uploaded files via mail'"
                    :disabled="!(item[header] > 0) || isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'mail')"
                    @click.prevent="sendUploadedMail(item)"
                  >
                    <i :class="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'mail') ? 'fa fa-spinner fa-spin zip-icon' : 'fa fa-envelope zip-icon'" aria-hidden="true"></i>
                  </button>
                </template>
                <!-- failed_file & successed_file: keep Excel export; failed_file keeps ZIP too -->
                <template v-else>
                  <button
                    class="excel-btn"
                    :title="`Export ${header}`"
                    :disabled="!(item[header] > 0)"
                    @click.prevent="downloadExcel(header, item)"
                  >
                    <i class="fa fa-file-excel excel-icon" aria-hidden="true"></i>
                  </button>
                  <button
                    v-if="header === 'failed_file'"
                    class="zip-btn"
                    :title="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip') ? 'Downloading...' : 'Download failed files (zip)'"
                    :disabled="!(item[header] > 0) || isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip')"
                    @click.prevent="downloadZip(item)"
                  >
                    <i :class="isDownloadingTrack(item.upload_track || item.uploadTrack || '', 'zip') ? 'fa fa-spinner fa-spin zip-icon' : 'fa fa-file-archive zip-icon'" aria-hidden="true"></i>
                  </button>
                </template>
              </template>
              <template v-else>
                {{ formatCell(header, item) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading">No data found.</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'LeoUploadedList',
  data() {
    return {
      fromdate: '',
      todate: '',
      leoList: [],
      headers: [],
      loading: false,
        error: '',
        downloadingTracks: {},
        // toast state
        showToast: false,
        toastTimer: null
    };
  },
  methods: {
    async fetchLeoUploadedList() {
      this.loading = true;
      this.error = '';
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          this.error = 'Missing access token. Please login again.';
          this.loading = false;
          return;
        }

        const params = {
          fromdate: this._formatDateForApi(this.fromdate),
          todate: this._formatDateForApi(this.todate)
        };

        const headers = { Authorization: `Bearer ${token}` };
        console.info('Requesting /leo_uploaded_list', { params, headers });
        const response = await axios.get('/leo_uploaded_list', { params, headers });
        console.log('API Response:', response);
        this.leoList = response.data[2] || [];
          this.headers = this._buildHeaders(this.leoList);
      } catch (err) {
        console.error('Fetch error:', err);
        if (err.response) {
          // server responded with a status code outside 2xx
          this.error = `Server ${err.response.status}: ${JSON.stringify(err.response.data)}`;
        } else if (err.request) {
          // request made but no response
          this.error = 'No response from server. Check backend or network.';
        } else {
          this.error = err.message || 'Failed to fetch data.';
        }
      } finally {
        this.loading = false;
      }
    },

    _formatDateForApi(dateStr) {
      if (!dateStr) return '';
      // if format is dd/mm/yyyy convert to yyyy-mm-dd
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [dd, mm, yyyy] = parts;
          return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
        }
      }
      return dateStr;
    },
    _buildHeaders(list) {
      if (!Array.isArray(list) || list.length === 0) return [];
      const keys = new Set();
      list.forEach(item => {
        if (item && typeof item === 'object') {
          Object.keys(item).forEach(k => keys.add(k));
        }
      });
      return Array.from(keys);
    },
    formatCell(header, item) {
      const val = item ? item[header] : undefined;
      if (val == null) return '';
      // handle create_date or created_date variants
      if (header === 'create_date' || header === 'created_date' || header.toLowerCase().includes('create')) {
        return this._formatCreateDate(val);
      }
      return val;
    },

    _formatCreateDate(value) {
      // Expecting ISO `yyyy-mm-ddTHH:MM:SS` or similar
      try {
        // If value already contains a space and slash, assume it's already formatted
        if (typeof value === 'string' && /\d{2}\/\d{2}\/\d{4}/.test(value)) return value;
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
      } catch (e) {
        return value;
      }
    },
    async downloadExcel(header, item) {
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        if (!token) {
          this.error = 'Missing access token. Please login again.';
          return;
        }
        // map the table column header to the backend reporttype parameter
        let reporttype = header;
        // specific mapping: successed_file should send reporttype=1, failed files=3
        if (header === 'successed_file') reporttype = '1';
        else if (header === 'failed_file') reporttype = '3';
        else if (header === 'uploaded_file') reporttype = '2';
        else reporttype = '2'; // default to uploaded_file
        const params = {
          upload_track: item.upload_track || item.uploadTrack || '',
          reporttype
        };
        const headers = { Authorization: `Bearer ${token}` };
        const resp = await axios.get('/generate_leo_excel', {
          params,
          headers,
          responseType: 'blob'
        });
        const blob = new Blob([resp.data], { type: resp.data.type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const filename = `${params.upload_track || 'leo'}_${reporttype}.xlsx`;
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export error:', err);
        this.error = err.response?.data?.detail || err.message || 'Export failed.';
      }
    },
    isDownloadingTrack(track, type = 'zip') {
      if (!track) return false;
      const entry = this.downloadingTracks && this.downloadingTracks[track];
      if (!entry) return false;
      // support legacy boolean value
      if (typeof entry === 'boolean') return entry;
      return !!entry[type];
    },

    async downloadZip(item) {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        this.error = 'Missing access token. Please login again.';
        return;
      }
      const track = item.upload_track || item.uploadTrack || '';
      if (!track) {
        this.error = 'Missing upload track for this row.';
        return;
      }
      if (this.isDownloadingTrack(track, 'zip')) {
        // already downloading this track
        return;
      }
      // mark as downloading (preserve other flags)
      this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), zip: true } };
      try {
        const params = { upload_track: track };
        const headers = { Authorization: `Bearer ${token}` };
        const resp = await axios.get('/download_failed_files_zip', {
          params,
          headers,
          responseType: 'blob'
        });
        const blob = new Blob([resp.data], { type: resp.data.type || 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const filename = `${params.upload_track || 'leo'}_failed_files.zip`;
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Zip export error:', err);
        this.error = err.response?.data?.detail || err.message || 'Export failed.';
      } finally {
        // clear downloading flag for zip
        this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), zip: false } };
      }
    },

    async downloadUploadedZip(item) {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        this.error = 'Missing access token. Please login again.';
        return;
      }
      const track = item.upload_track || item.uploadTrack || '';
      if (!track) {
        this.error = 'Missing upload track for this row.';
        return;
      }
      if (this.isDownloadingTrack(track, 'zip')) return;
      this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), zip: true } };
      try {
        const params = { upload_track: track };
        const headers = { Authorization: `Bearer ${token}` };
        // endpoint for uploaded files zip (adjust server path if different)
        const resp = await axios.get('/download_uploaded_files_zip', { params, headers, responseType: 'blob' });
        let blob = new Blob([resp.data], { type: resp.data.type || 'application/zip' });
        if (blob.size === 0) {
          const resp2 = await axios.get('/download_uploaded_files_zip', { params, headers, responseType: 'arraybuffer' });
          const blob2 = new Blob([resp2.data], { type: 'application/zip' });
          if (blob2.size > 0) blob = blob2;
          else {
            try {
              const text = new TextDecoder().decode(resp2.data);
              this.error = 'Download returned empty file. Server message: ' + (text.slice ? text.slice(0, 500) : text);
            } catch (e) {
              this.error = 'Download returned empty file.';
            }
            return;
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const filename = `${track || 'leo'}_uploaded_files.zip`;
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Uploaded zip export error:', err);
        this.error = err.response?.data?.detail || err.message || 'Export failed.';
      } finally {
        this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), zip: false } };
      }
    },

    async sendUploadedMail(item) {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!token) {
        this.error = 'Missing access token. Please login again.';
        return;
      }
      const track = item.upload_track || item.uploadTrack || '';
      if (!track) {
        this.error = 'Missing upload track for this row.';
        return;
      }
      if (this.isDownloadingTrack(track, 'mail')) return;
      this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), mail: true } };
      try {
        const params = { upload_track: track };
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const resp = await axios.get('/email_uploaded_files', { params, headers });
        // assume API returns { success: true/false, message }
        if (resp && resp.data && resp.data.success) {
          this.error = '';
          this.error = resp.data.message || 'Email queued/sent successfully.';
        } else {
          this.error = resp.data?.message || 'Failed to queue email.';
        }
      } catch (err) {
        console.error('Send mail error:', err);
        this.error = err.response?.data?.detail || err.message || 'Send mail failed.';
      } finally {
        this.downloadingTracks = { ...(this.downloadingTracks || {}), [track]: { ...(this.downloadingTracks[track] || {}), mail: false } };
      }
    },
    closeToast() {
      this.showToast = false;
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.error = '';
    }
  },
  watch: {
    error(newVal) {
      if (newVal) {
        this.showToast = true;
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = setTimeout(() => {
          this.showToast = false;
          this.toastTimer = null;
          this.error = '';
        }, 5000);
      }
    }
  },
  mounted() {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const formatted = `${dd}/${mm}/${yyyy}`;
    this.fromdate = formatted;
    this.todate = formatted;
    this.fetchLeoUploadedList();
  }
};
</script>

<style scoped>
.leo-uploaded-list {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 80vh;
  background: #f7f7f7;
  padding: 30px 0;
}
.card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 32px 40px;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-width: 0;
  margin: 0;
}
.title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 32px;
  text-align: center;
}
.date-form {
  display: flex;
  gap: 26px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
}
.form-group {
  display: flex;
  flex-direction: column;
  flex: 1;
}
label {
  font-weight: 500;
  margin-bottom: 8px;
}
.required {
  color: #e74c3c;
  margin-left: 2px;
}
.date-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
  margin-top: 4px;
  outline: none;
  transition: border 0.2s;
}
.date-input:focus {
  border: 1.5px solid #007bff;
}
.fetch-btn {
  padding: 10px 28px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;
  margin-top: 0;
  height: 42px;
}
.fetch-btn:hover {
  background: #0056b3;
}
.error {
  color: #e74c3c;
  margin-top: 10px;
}
.excel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: 8px;
  padding: 6px;
  border-radius: 4px;
}
.excel-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.excel-icon {
  color: #107C10;
  font-size: 16px;
  line-height: 1;
}
.zip-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: 6px;
  padding: 6px;
  border-radius: 4px;
}
.zip-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.zip-icon {
  color: #b12704;
  font-size: 15px;
}
/* Toast styles */
.toast {
  position: fixed;
  right: 20px;
  top: 20px;
  background: #333;
  color: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.15);
  z-index: 9999;
  min-width: 220px;
}
.toast-message {
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.2;
}
.toast-close {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 6px;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 32px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
th, td {
  border: 1px solid #eaeaea;
  padding: 10px 12px;
  text-align: left;
}
th {
  background: #f5f6fa;
  font-weight: 600;
}
</style>
